/**
 * Knowledge Graph Store
 *
 * Manages knowledge graph state and operations
 */
import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'
import type {
  KnowledgeGraph,
  KnowledgeGraphCreate,
  KnowledgeGraphUpdate,
  GraphNode,
  GraphNodeCreate,
  GraphNodeUpdate,
  GraphRelationship,
  GraphRelationshipCreate,
  GraphVisualizationData,
  GraphStatistics,
  GraphType,
  GraphStatus,
  NodeType,
  RelationType,
  PaginatedResponse,
} from '@/types'

class KnowledgeGraphStore {
  graphs: KnowledgeGraph[] = []
  selectedGraph: KnowledgeGraph | null = null

  // Nodes and relationships for selected graph
  nodes: GraphNode[] = []
  relationships: GraphRelationship[] = []

  // Visualization data
  visualizationData: GraphVisualizationData | null = null
  statistics: GraphStatistics | null = null

  // UI state
  isLoading = false
  error: string | null = null

  // Pagination
  currentPage = 1
  pageSize = 20
  totalGraphs = 0

  // Filters
  searchQuery = ''
  graphTypeFilter: GraphType | 'all' = 'all'
  statusFilter: GraphStatus | 'all' = 'all'
  workspaceFilter: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  // State management
  setSearchQuery(query: string) {
    this.searchQuery = query
    this.currentPage = 1
  }

  setGraphTypeFilter(type: GraphType | 'all') {
    this.graphTypeFilter = type
    this.currentPage = 1
  }

  setStatusFilter(status: GraphStatus | 'all') {
    this.statusFilter = status
    this.currentPage = 1
  }

  setWorkspaceFilter(workspaceId: string | null) {
    this.workspaceFilter = workspaceId
    this.currentPage = 1
  }

  setPage(page: number) {
    this.currentPage = page
  }

  clearFilters() {
    this.searchQuery = ''
    this.graphTypeFilter = 'all'
    this.statusFilter = 'all'
    this.workspaceFilter = null
    this.currentPage = 1
  }

  clearError() {
    this.error = null
  }

  get filteredGraphs() {
    let filtered = this.graphs

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase()
      filtered = filtered.filter((graph) =>
        graph.name.toLowerCase().includes(query) ||
        (graph.description?.toLowerCase().includes(query))
      )
    }

    if (this.graphTypeFilter !== 'all') {
      filtered = filtered.filter((graph) => graph.graph_type === this.graphTypeFilter)
    }

    if (this.statusFilter !== 'all') {
      filtered = filtered.filter((graph) => graph.status === this.statusFilter)
    }

    return filtered
  }

  // Graph CRUD Operations
  async fetchGraphs(workspaceId?: string) {
    this.isLoading = true
    this.error = null

    try {
      const params: any = {
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
      }

      if (workspaceId || this.workspaceFilter) {
        params.workspace_id = workspaceId || this.workspaceFilter
      }

      if (this.graphTypeFilter !== 'all') {
        params.graph_type = this.graphTypeFilter
      }

      if (this.statusFilter !== 'all') {
        params.status = this.statusFilter
      }

      const response: PaginatedResponse<KnowledgeGraph> = await apiClient.getKnowledgeGraphs(params)

      runInAction(() => {
        this.graphs = response.items
        this.totalGraphs = response.total
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch graphs'
        this.isLoading = false
      })
      throw error
    }
  }

  async fetchGraphById(graphId: string) {
    this.isLoading = true
    this.error = null

    try {
      const graph = await apiClient.getKnowledgeGraph(graphId)

      runInAction(() => {
        this.selectedGraph = graph
        this.isLoading = false
      })

      // Also fetch nodes, relationships, and statistics
      await Promise.all([
        this.fetchNodes(graphId),
        this.fetchRelationships(graphId),
        this.fetchStatistics(graphId),
      ])

      return graph
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch graph'
        this.isLoading = false
      })
      throw error
    }
  }

  async createGraph(data: KnowledgeGraphCreate) {
    this.isLoading = true
    this.error = null

    try {
      const graph = await apiClient.createKnowledgeGraph(data)

      runInAction(() => {
        this.graphs.unshift(graph)
        this.totalGraphs += 1
        this.selectedGraph = graph
        this.isLoading = false
      })

      return graph
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to create graph'
        this.isLoading = false
      })
      throw error
    }
  }

  async updateGraph(graphId: string, data: KnowledgeGraphUpdate) {
    this.isLoading = true
    this.error = null

    try {
      const graph = await apiClient.updateKnowledgeGraph(graphId, data)

      runInAction(() => {
        const index = this.graphs.findIndex((g) => g.id === graphId)
        if (index !== -1) {
          this.graphs[index] = graph
        }
        if (this.selectedGraph?.id === graphId) {
          this.selectedGraph = graph
        }
        this.isLoading = false
      })

      return graph
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to update graph'
        this.isLoading = false
      })
      throw error
    }
  }

  async deleteGraph(graphId: string) {
    this.isLoading = true
    this.error = null

    try {
      await apiClient.deleteKnowledgeGraph(graphId)

      runInAction(() => {
        this.graphs = this.graphs.filter((g) => g.id !== graphId)
        this.totalGraphs -= 1
        if (this.selectedGraph?.id === graphId) {
          this.selectedGraph = null
        }
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to delete graph'
        this.isLoading = false
      })
      throw error
    }
  }

  // Node Operations
  async fetchNodes(graphId: string, params?: { node_types?: NodeType[]; limit?: number }) {
    try {
      const nodes = await apiClient.getGraphNodes(graphId, params)

      runInAction(() => {
        this.nodes = nodes
      })

      return nodes
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch nodes'
      })
      throw error
    }
  }

  async createNode(graphId: string, data: GraphNodeCreate) {
    this.isLoading = true
    this.error = null

    try {
      const node = await apiClient.createGraphNode(graphId, data)

      runInAction(() => {
        this.nodes.push(node)
        if (this.selectedGraph) {
          this.selectedGraph.node_count += 1
        }
        this.isLoading = false
      })

      // Refresh visualization
      await this.fetchVisualization(graphId)

      return node
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to create node'
        this.isLoading = false
      })
      throw error
    }
  }

  async updateNode(graphId: string, nodeId: string, data: GraphNodeUpdate) {
    try {
      const node = await apiClient.updateGraphNode(graphId, nodeId, data)

      runInAction(() => {
        const index = this.nodes.findIndex((n) => n.id === nodeId)
        if (index !== -1) {
          this.nodes[index] = node
        }
      })

      return node
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to update node'
      })
      throw error
    }
  }

  async deleteNode(graphId: string, nodeId: string) {
    this.isLoading = true
    this.error = null

    try {
      await apiClient.deleteGraphNode(graphId, nodeId)

      runInAction(() => {
        this.nodes = this.nodes.filter((n) => n.id !== nodeId)
        if (this.selectedGraph) {
          this.selectedGraph.node_count -= 1
        }
        this.isLoading = false
      })

      // Refresh visualization
      await this.fetchVisualization(graphId)
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to delete node'
        this.isLoading = false
      })
      throw error
    }
  }

  // Relationship Operations
  async fetchRelationships(graphId: string, params?: { relation_types?: RelationType[]; limit?: number }) {
    try {
      const relationships = await apiClient.getGraphRelationships(graphId, params)

      runInAction(() => {
        this.relationships = relationships
      })

      return relationships
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch relationships'
      })
      throw error
    }
  }

  async createRelationship(graphId: string, data: GraphRelationshipCreate) {
    this.isLoading = true
    this.error = null

    try {
      const relationship = await apiClient.createGraphRelationship(graphId, data)

      runInAction(() => {
        this.relationships.push(relationship)
        if (this.selectedGraph) {
          this.selectedGraph.edge_count += 1
        }
        this.isLoading = false
      })

      // Refresh visualization
      await this.fetchVisualization(graphId)

      return relationship
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to create relationship'
        this.isLoading = false
      })
      throw error
    }
  }

  async deleteRelationship(graphId: string, relationshipId: string) {
    this.isLoading = true
    this.error = null

    try {
      await apiClient.deleteGraphRelationship(graphId, relationshipId)

      runInAction(() => {
        this.relationships = this.relationships.filter((r) => r.id !== relationshipId)
        if (this.selectedGraph) {
          this.selectedGraph.edge_count -= 1
        }
        this.isLoading = false
      })

      // Refresh visualization
      await this.fetchVisualization(graphId)
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to delete relationship'
        this.isLoading = false
      })
      throw error
    }
  }

  // Query Operations
  async searchNodes(graphId: string, query: string, nodeTypes?: NodeType[], limit: number = 20) {
    this.isLoading = true
    this.error = null

    try {
      const response = await apiClient.searchGraphNodes(graphId, {
        query,
        node_types: nodeTypes,
        limit,
      })

      runInAction(() => {
        this.isLoading = false
      })

      return response.nodes
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to search nodes'
        this.isLoading = false
      })
      throw error
    }
  }

  async findPath(graphId: string, sourceNodeId: string, targetNodeId: string, maxDepth: number = 5) {
    this.isLoading = true
    this.error = null

    try {
      const response = await apiClient.findGraphPath(graphId, {
        source_node_id: sourceNodeId,
        target_node_id: targetNodeId,
        max_depth: maxDepth,
      })

      runInAction(() => {
        this.isLoading = false
      })

      return response
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to find path'
        this.isLoading = false
      })
      throw error
    }
  }

  async getNeighbors(graphId: string, nodeId: string, direction: string = 'both', limit: number = 50) {
    try {
      const neighbors = await apiClient.getGraphNeighbors(graphId, nodeId, { direction, limit })
      return neighbors
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to get neighbors'
      })
      throw error
    }
  }

  // Visualization
  async fetchVisualization(
    graphId: string,
    options?: {
      root_node_id?: string
      depth?: number
      node_types?: NodeType[]
      limit?: number
    }
  ) {
    this.isLoading = true
    this.error = null

    try {
      const data = await apiClient.getGraphVisualization(graphId, {
        root_node_id: options?.root_node_id,
        depth: options?.depth || 2,
        node_types: options?.node_types,
        limit: options?.limit || 100,
      })

      runInAction(() => {
        this.visualizationData = data
        this.isLoading = false
      })

      return data
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch visualization'
        this.isLoading = false
      })
      throw error
    }
  }

  // Statistics
  async fetchStatistics(graphId: string) {
    try {
      const stats = await apiClient.getGraphStatistics(graphId)

      runInAction(() => {
        this.statistics = stats
      })

      return stats
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch statistics'
      })
      throw error
    }
  }

  // ==========================================================================
  // GraphRAG Operations (Weaviate + Memgraph hybrid retrieval)
  // ==========================================================================

  graphRAGStatus: {
    available: boolean
    message: string
    weaviate_connected: boolean
    memgraph_connected: boolean
  } | null = null

  graphRAGResults: {
    query: string
    mode: string
    documents: any[]
    entities: any[]
    relationships: any[]
    temporal_facts: any[]
    agent_memories: any[]
    agent_decisions: any[]
    total_results: number
    processing_time_ms: number
    context_string?: string
  } | null = null

  agentMemories: any[] = []
  agentDecisions: any[] = []

  async checkGraphRAGStatus() {
    try {
      const status = await apiClient.getGraphRAGStatus()

      runInAction(() => {
        this.graphRAGStatus = status
      })

      return status
    } catch (error: any) {
      runInAction(() => {
        this.graphRAGStatus = {
          available: false,
          message: error.response?.data?.detail || 'Failed to check GraphRAG status',
          weaviate_connected: false,
          memgraph_connected: false,
        }
      })
      throw error
    }
  }

  async queryGraphRAG(params: {
    query: string
    graph_id?: string
    project_id?: string
    agent_id?: string
    mode?: 'semantic_only' | 'graph_only' | 'hybrid' | 'graph_enhanced'
    semantic_limit?: number
    graph_depth?: number
    include_memories?: boolean
  }) {
    this.isLoading = true
    this.error = null

    try {
      const results = await apiClient.queryGraphRAG(params)

      runInAction(() => {
        this.graphRAGResults = results
        this.isLoading = false
      })

      return results
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to query GraphRAG'
        this.isLoading = false
      })
      throw error
    }
  }

  async addGraphRAGEntity(params: {
    graph_id?: string
    name: string
    entity_type?: string
    description?: string
    properties?: Record<string, any>
    project_id?: string
  }) {
    this.isLoading = true
    this.error = null

    try {
      const result = await apiClient.addGraphRAGEntity(params)

      runInAction(() => {
        this.isLoading = false
      })

      return result
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to add entity'
        this.isLoading = false
      })
      throw error
    }
  }

  async addGraphRAGRelationship(params: {
    graph_id?: string
    from_entity_id: string
    to_entity_id: string
    relationship_type: string
    properties?: Record<string, any>
  }) {
    this.isLoading = true
    this.error = null

    try {
      const result = await apiClient.addGraphRAGRelationship(params)

      runInAction(() => {
        this.isLoading = false
      })

      return result
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to add relationship'
        this.isLoading = false
      })
      throw error
    }
  }

  async storeAgentMemory(params: {
    graph_id?: string
    agent_id: string
    content: string
    memory_type?: 'episodic' | 'semantic' | 'procedural' | 'decision'
    context?: Record<string, any>
    importance?: number
    project_id?: string
  }) {
    this.isLoading = true
    this.error = null

    try {
      const result = await apiClient.storeAgentMemory(params)

      runInAction(() => {
        this.isLoading = false
      })

      return result
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to store agent memory'
        this.isLoading = false
      })
      throw error
    }
  }

  async fetchAgentMemories(agentId: string, params?: { graph_id?: string; memory_type?: string; limit?: number }) {
    this.isLoading = true
    this.error = null

    try {
      const result = await apiClient.getAgentMemories(agentId, params)

      runInAction(() => {
        this.agentMemories = result.memories
        this.isLoading = false
      })

      return result
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch agent memories'
        this.isLoading = false
      })
      throw error
    }
  }

  async fetchAgentDecisions(agentId: string, params?: { graph_id?: string; limit?: number }) {
    this.isLoading = true
    this.error = null

    try {
      const result = await apiClient.getAgentDecisions(agentId, params)

      runInAction(() => {
        this.agentDecisions = result.decisions
        this.isLoading = false
      })

      return result
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch agent decisions'
        this.isLoading = false
      })
      throw error
    }
  }

  async listGraphRAGEntities(graphId: string, params?: { entity_type?: string; limit?: number }) {
    this.isLoading = true
    this.error = null

    try {
      const result = await apiClient.listGraphRAGEntities(graphId, params)

      runInAction(() => {
        this.isLoading = false
      })

      return result
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to list entities'
        this.isLoading = false
      })
      throw error
    }
  }

  async listGraphRAGRelationships(graphId: string, params?: { relationship_type?: string; limit?: number }) {
    this.isLoading = true
    this.error = null

    try {
      const result = await apiClient.listGraphRAGRelationships(graphId, params)

      runInAction(() => {
        this.isLoading = false
      })

      return result
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to list relationships'
        this.isLoading = false
      })
      throw error
    }
  }

  // Reset
  reset() {
    this.graphs = []
    this.selectedGraph = null
    this.nodes = []
    this.relationships = []
    this.visualizationData = null
    this.statistics = null
    this.isLoading = false
    this.error = null
    this.currentPage = 1
    this.totalGraphs = 0
    this.clearFilters()

    // Reset GraphRAG state
    this.graphRAGStatus = null
    this.graphRAGResults = null
    this.agentMemories = []
    this.agentDecisions = []
  }
}

export const knowledgeGraphStore = new KnowledgeGraphStore()
export default knowledgeGraphStore
